import { useMemo, type ReactElement } from 'react'
import { isNotEmpty } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import { useQuestions, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, TButton } from '@components'
import { QuestionListSection } from './QuestionListSection'
import { groupQuestionsByStatus } from './groupQuestionsByStatus'

function QuestionsPage(): ReactElement {
  const { t } = useTranslation()
  const { data: questions, refetch, isFetching, isLoading, error } = useQuestions()
  useToastError(error)

  const { active, upcoming, ended } = useMemo(() => groupQuestionsByStatus(questions ?? []), [questions])

  if (isLoading) return <NotReady data={questions} error={error} id='Questions' isLoading={isLoading} />

  const questionsExist = isNotEmpty(questions)

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Startup Questions')}>
        {questionsExist && (
          <ButtonAdd component={Link} to='add'>
            {t('Add Question')}
          </ButtonAdd>
        )}
        <TButton component={Link} icon='archive' to='trash' tooltip={t('View Trash')} variant='secondary' />
      </Page.Heading>
      <Page.Content scrollArea>
        {questionsExist && (
          <Stack gap='xl'>
            <QuestionListSection
              emptyMessage={t('No questions are currently running')}
              questions={active}
              title={`${t('Active')} (${active.length})`}
            />
            {isNotEmpty(upcoming) && (
              <QuestionListSection questions={upcoming} title={`${t('Upcoming')} (${upcoming.length})`} />
            )}
            {isNotEmpty(ended) && <QuestionListSection questions={ended} title={`${t('Ended')} (${ended.length})`} />}
          </Stack>
        )}
        {!questionsExist && (
          <Page.Hero icon='help' title={t('No Startup Questions')}>
            <ButtonAdd component={Link} to='add'>
              {t('Add Question')}
            </ButtonAdd>
          </Page.Hero>
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/questions/')({
  component: QuestionsPage,
})
